import { zodResolver } from '@hookform/resolvers/zod';
import {
  organizationSettingsSchema,
  passwordPolicySchema,
  type OrganizationSettings,
} from '@qualicloud/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  Card,
  CardHeader,
  DatePicker,
  Field,
  FormError,
  IconAlertTriangle,
  IconCheckCircle,
  IconInfo,
  IconShieldCheck,
  Input,
  NumberInput,
  Select,
  Skeleton,
  Switch,
  Tabs,
  cn,
  useToast,
} from '@/design-system';
import { organizationsApi, queryKeys, toUserMessage } from '@/lib/api';
import {
  formatDateOnly,
  formatTimeOfDay,
  timeZoneAbbreviation,
  timeZoneLongName,
} from '@/lib/format/datetime';
import { useAuth } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/** Somente `slug` é imutável; o resto do schema do contrato é editável. */
const settingsFormSchema = organizationSettingsSchema.omit({ slug: true });
type SettingsValues = z.infer<typeof settingsFormSchema>;

/** Fusos brasileiros mais comuns + UTC, para o seletor. */
const TIME_ZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Cuiaba',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Rio_Branco',
  'America/Noronha',
  'UTC',
] as const;

/**
 * Administração da organização.
 *
 * Reúne tudo que é configuração REGULADA e que, no legado, ou não existia ou
 * estava espalhado em constantes de código:
 *   · política de senha (§11.300(b));
 *   · retenção de registros (ISO 13485 §4.2.5 / RDC 665);
 *   · fuso de exibição (toda data regulada é exibida nele);
 *   · autoaprovação / segregação de funções (ISO 13485 §4.2.4);
 *   · registro da carta ao FDA (§11.100(c));
 *   · SMTP para as notificações.
 *
 * Toda alteração aqui gera evento `CONFIG_CHANGE` na trilha de auditoria.
 */
export function OrganizationPage() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useState('geral');

  const organizationId = organization?.id ?? '';

  const detailQuery = useQuery({
    queryKey: queryKeys.organization.detail(organizationId),
    queryFn: () => organizationsApi.detail(organizationId),
    enabled: Boolean(organizationId),
  });

  const data = detailQuery.data;

  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsFormSchema),
    values: data
      ? {
          name: data.name,
          timeZone: data.timeZone,
          locale: data.locale,
          passwordPolicy: passwordPolicySchema.parse(data.passwordPolicy ?? {}),
          allowSelfApproval: data.allowSelfApproval,
          signatureSessionWindowMinutes: data.signatureSessionWindowMinutes,
          recordRetentionYears: data.recordRetentionYears,
          documentExpiryWarningDays: data.documentExpiryWarningDays,
          part11CertificationLetterSentAt: data.part11CertificationLetterSentAt
            ? new Date(data.part11CertificationLetterSentAt)
            : null,
          smtp: data.smtp,
        }
      : undefined,
  });

  const save = useMutation({
    mutationFn: (values: SettingsValues) =>
      organizationsApi.update(organizationId, values as Partial<OrganizationSettings>),
    onError: (error) => form.setError('root', { message: toUserMessage(error) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.organization.all() });
      toast({
        tone: 'success',
        title: 'Configurações salvas',
        description: 'A alteração foi registrada na trilha de auditoria da organização.',
      });
    },
  });

  if (detailQuery.isPending) {
    return (
      <PageBody>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-4 h-96 w-full" />
      </PageBody>
    );
  }

  const onSubmit = () => void form.handleSubmit((values) => save.mutate(values))();

  return (
    <>
      <PageHeader
        title="Administração da organização"
        description={`Configurações reguladas de ${data?.name ?? ''}. Toda alteração é registrada na trilha de auditoria.`}
        actions={
          <Button variant="primary" loading={save.isPending} onClick={onSubmit}>
            Salvar alterações
          </Button>
        }
      />

      <PageBody>
        {form.formState.errors.root ? (
          <FormError title="As configurações não foram salvas" className="mb-4">
            {form.formState.errors.root.message}
          </FormError>
        ) : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
          noValidate
        >
          <Tabs
            ariaLabel="Seções da configuração da organização"
            value={tab}
            onValueChange={setTab}
            tabs={[
              { value: 'geral', label: 'Geral', content: <GeneralSection form={form} slug={data?.slug ?? ''} /> },
              { value: 'senha', label: 'Política de senha', content: <PasswordPolicySection form={form} /> },
              {
                value: 'conformidade',
                label: 'Conformidade',
                content: <ComplianceSection form={form} />,
              },
              { value: 'email', label: 'Envio de e-mail', content: <SmtpSection form={form} /> },
            ]}
          />
        </form>
      </PageBody>
    </>
  );
}

type FormApi = ReturnType<typeof useForm<SettingsValues>>;

function GeneralSection({ form, slug }: { readonly form: FormApi; readonly slug: string }) {
  const timeZone = form.watch('timeZone');
  /*
   * `null` enquanto o formulário não tiver um fuso utilizável — e ele não tem
   * no primeiro quadro depois da resposta, porque o `Controller` do
   * react-hook-form devolve string vazia para campo ainda sem valor. Formatar
   * essa string levantava `RangeError` e o ErrorBoundary engolia a página
   * inteira: quem abria "Organização" via a tela de erro, sem nenhum select.
   */
  const agora = formatTimeOfDay(new Date(), timeZone);

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader headingLevel={3} title="Identificação" />
        <div className="mt-4 flex flex-col gap-4">
          <Field label="Nome da organização" required error={form.formState.errors.name?.message}>
            <Input {...form.register('name')} maxLength={255} />
          </Field>

          <Field
            label="Identificador (slug)"
            description="Usado na entrada quando o usuário existe em mais de uma organização. Não pode ser alterado — mudá-lo quebraria referências históricas."
          >
            <Input value={slug} readOnly disabled className="font-mono" />
          </Field>

          <Controller
            control={form.control}
            name="locale"
            render={({ field }) => (
              <Field label="Idioma padrão">
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    { value: 'pt-BR', label: 'Português (Brasil)' },
                    { value: 'en-US', label: 'English (US)' },
                  ]}
                />
              </Field>
            )}
          />
        </div>
      </Card>

      <Card>
        <CardHeader
          headingLevel={3}
          title="Fuso horário"
          description="Todas as datas e horas exibidas no sistema usam este fuso, independentemente de onde o usuário esteja."
        />
        <div className="mt-4 flex flex-col gap-4">
          <Controller
            control={form.control}
            name="timeZone"
            render={({ field }) => (
              <Field label="Fuso da organização" required>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={TIME_ZONES.map((zone) => ({
                    value: zone,
                    label: zone,
                    description: timeZoneLongName(zone),
                  }))}
                />
              </Field>
            )}
          />

          <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
            <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              {agora ? (
                <>
                  Agora são <strong className="font-semibold qc-tabular">{agora}</strong> (
                  {timeZoneAbbreviation(new Date(), timeZone)}).{' '}
                </>
              ) : null}
              Registros assinados guardam o fuso vigente no momento do ato: mudar esta configuração
              não altera carimbos históricos.
            </span>
          </p>

          <Field
            label="Aviso de vencimento (dias)"
            error={form.formState.errors.documentExpiryWarningDays?.message}
            description="Antecedência com que documentos próximos do vencimento são destacados e notificados."
          >
            <NumberInput
              {...form.register('documentExpiryWarningDays')}
              min={1}
              max={365}
              className="w-28"
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

function PasswordPolicySection({ form }: { readonly form: FormApi }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader
          headingLevel={3}
          title="Complexidade"
          description="Requisitos verificados no servidor a cada troca de senha. 21 CFR §11.300(b)."
        />
        <div className="mt-4 flex flex-col gap-4">
          <Field label="Comprimento mínimo" required>
            <NumberInput
              {...form.register('passwordPolicy.minLength')}
              min={8}
              max={128}
              className="w-24"
            />
          </Field>

          {(
            [
              ['passwordPolicy.requireUppercase', 'Exigir letra maiúscula'],
              ['passwordPolicy.requireLowercase', 'Exigir letra minúscula'],
              ['passwordPolicy.requireDigit', 'Exigir número'],
              ['passwordPolicy.requireSymbol', 'Exigir símbolo'],
            ] as const
          ).map(([name, label]) => (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                  stateLabels={{ on: 'Exigido', off: 'Não exigido' }}
                  label={label}
                />
              )}
            />
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader
          headingLevel={3}
          title="Validade e bloqueio"
          description="Expiração periódica e bloqueio progressivo por tentativas malsucedidas. 21 CFR §11.300(b) e (d)."
        />
        <div className="mt-4 flex flex-col gap-4">
          <Field
            label="Expiração da senha (dias)"
            description="Zero desliga a expiração periódica."
          >
            <NumberInput {...form.register('passwordPolicy.expiryDays')} min={0} max={3650} className="w-28" />
          </Field>

          <Field
            label="Senhas anteriores bloqueadas"
            description="Quantas senhas antigas não podem ser reutilizadas."
          >
            <NumberInput {...form.register('passwordPolicy.historySize')} min={0} max={50} className="w-24" />
          </Field>

          <Field label="Tentativas antes do bloqueio">
            <NumberInput
              {...form.register('passwordPolicy.maxFailedAttempts')}
              min={3}
              max={20}
              className="w-24"
            />
          </Field>

          <Field label="Duração do bloqueio (minutos)">
            <NumberInput
              {...form.register('passwordPolicy.lockoutMinutes')}
              min={1}
              max={1440}
              className="w-28"
            />
          </Field>

          <Field
            label="Multiplicador de bloqueio"
            description="A cada novo ciclo de falhas, a duração do bloqueio é multiplicada por este fator."
          >
            <NumberInput
              {...form.register('passwordPolicy.lockoutBackoffFactor')}
              min={1}
              max={10}
              step={0.5}
              className="w-24"
            />
          </Field>

          <Controller
            control={form.control}
            name="passwordPolicy.mfaRequired"
            render={({ field }) => (
              <Switch
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
                stateLabels={{ on: 'Obrigatório', off: 'Opcional' }}
                label="Segundo fator obrigatório"
                description="Quando ativo, o segundo fator é exigido na entrada E em cada assinatura eletrônica."
              />
            )}
          />
        </div>
      </Card>
    </div>
  );
}

function ComplianceSection({ form }: { readonly form: FormApi }) {
  const letterSentAt = form.watch('part11CertificationLetterSentAt');
  const allowSelfApproval = form.watch('allowSelfApproval');

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader
          headingLevel={3}
          title="Assinatura e segregação de funções"
          description="Controles exigidos pela ISO 13485 §4.2.4 e pelo 21 CFR Part 11."
        />
        <div className="mt-4 flex flex-col gap-4">
          <Controller
            control={form.control}
            name="allowSelfApproval"
            render={({ field }) => (
              <Switch
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
                stateLabels={{ on: 'Permitida', off: 'Bloqueada' }}
                label="Permitir que o autor aprove a própria versão"
                description="O padrão é bloquear. A segregação entre quem elabora e quem aprova é o controle esperado em inspeção."
              />
            )}
          />

          {allowSelfApproval ? (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
            >
              <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                <strong className="font-semibold">Atenção.</strong> Com a autoaprovação liberada, o
                mesmo colaborador pode elaborar e aprovar uma versão. Isso costuma ser questionado
                em auditoria e exige justificativa documentada na análise de risco da organização.
              </span>
            </p>
          ) : null}

          <Field
            label="Janela de sessão de assinatura (minutos)"
            description="Período em que assinaturas consecutivas dispensam nova digitação da senha. Zero — o padrão — exige a credencial completa em cada ato, que é o comportamento mais defensável (§11.200(a)(1)(i))."
          >
            <NumberInput
              {...form.register('signatureSessionWindowMinutes')}
              min={0}
              max={480}
              className="w-28"
            />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader
          headingLevel={3}
          title="Retenção e certificação"
          description="Prazo de guarda dos registros e o registro da carta obrigatória ao FDA."
        />
        <div className="mt-4 flex flex-col gap-4">
          <Field
            label="Retenção de registros (anos)"
            error={form.formState.errors.recordRetentionYears?.message}
            description="Mínimo de 2 anos por norma; 10 anos é o padrão conservador adotado. Arquivos são gravados com bloqueio de escrita por esse período."
          >
            <NumberInput {...form.register('recordRetentionYears')} min={2} max={50} className="w-24" />
          </Field>

          <Controller
            control={form.control}
            name="part11CertificationLetterSentAt"
            render={({ field }) => (
              <Field
                label="Carta de certificação ao FDA — data de envio"
                description="21 CFR §11.100(c) exige que a organização envie ao FDA uma carta certificando que suas assinaturas eletrônicas são o equivalente legal das manuscritas. É obrigação do cliente, não do fornecedor — mas sem ela as assinaturas não têm valor."
              >
                <DatePicker
                  value={field.value ? toIsoDate(field.value) : ''}
                  onValueChange={(iso) => field.onChange(iso ? new Date(`${iso}T00:00:00`) : null)}
                  className="max-w-[14rem]"
                />
              </Field>
            )}
          />

          <div
            className={cn(
              'flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm',
              letterSentAt
                ? 'border-success bg-success-subtle text-success-content'
                : 'border-danger bg-danger-subtle text-danger-content',
            )}
            role={letterSentAt ? undefined : 'alert'}
          >
            {letterSentAt ? (
              <IconCheckCircle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            ) : (
              <IconShieldCheck size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            )}
            <span>
              {letterSentAt ? (
                <>
                  Carta registrada como enviada em{' '}
                  <strong className="font-semibold qc-tabular">
                    {formatDateOnly(toIsoDate(letterSentAt))}
                  </strong>
                  . Guarde o comprovante de envio junto da documentação de validação.
                </>
              ) : (
                <>
                  <strong className="font-semibold">Carta ainda não registrada.</strong> Enquanto o
                  envio não for confirmado, as assinaturas eletrônicas produzidas nesta organização
                  podem ser contestadas quanto ao valor legal perante o FDA.
                </>
              )}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}

function SmtpSection({ form }: { readonly form: FormApi }) {
  const smtp = form.watch('smtp');
  const enabled = smtp !== null;

  return (
    <Card className="max-w-2xl">
      <CardHeader
        headingLevel={3}
        title="Servidor de e-mail"
        description="Usado para enviar notificações de aprovação pendente, vencimento e ciência. Sem SMTP configurado, as notificações ficam apenas na caixa interna."
      />

      <div className="mt-4 flex flex-col gap-4">
        <Switch
          checked={enabled}
          onCheckedChange={(checked) =>
            form.setValue(
              'smtp',
              checked
                ? { host: '', port: 587, secure: true, username: '', fromName: '', fromAddress: '' }
                : null,
            )
          }
          stateLabels={{ on: 'Configurado', off: 'Desativado' }}
          label="Enviar notificações por e-mail"
        />

        {enabled ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Servidor (host)" required>
              <Input {...form.register('smtp.host')} placeholder="smtp.exemplo.com.br" />
            </Field>
            <Field label="Porta" required>
              <NumberInput {...form.register('smtp.port')} min={1} max={65535} />
            </Field>
            <Field label="Usuário" required>
              <Input {...form.register('smtp.username')} autoComplete="off" />
            </Field>
            <div className="flex items-end pb-1">
              <Controller
                control={form.control}
                name="smtp.secure"
                render={({ field }) => (
                  <Switch
                    checked={Boolean(field.value)}
                    onCheckedChange={field.onChange}
                    stateLabels={{ on: 'TLS', off: 'Sem TLS' }}
                    label="Conexão segura"
                    description="Recomendado manter ativo."
                    className="w-full"
                  />
                )}
              />
            </div>
            <Field label="Nome do remetente" required>
              <Input {...form.register('smtp.fromName')} placeholder="Qualidade — Minha Empresa" />
            </Field>
            <Field label="E-mail do remetente" required>
              <Input
                {...form.register('smtp.fromAddress')}
                type="email"
                placeholder="qualidade@exemplo.com.br"
              />
            </Field>

            <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content sm:col-span-2">
              <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                A senha do SMTP não é editada nesta tela: ela é um segredo e vive fora do banco da
                aplicação, montada como variável de ambiente no serviço de notificações.
              </span>
            </p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function toIsoDate(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}
